import { useAutoneTranslation } from '@autone/translations';
import { useCallback, useLayoutEffect, useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../atoms';
import { cn } from '../../../../utils';
import { useAdvancedFiltersInternalContext } from '../contexts/advanced-filters-internal.context';

interface AdvancedFiltersOperatorSelectProps {
  rowId: string;
}

export const AdvancedFiltersOperatorSelect = ({
  rowId,
}: AdvancedFiltersOperatorSelectProps) => {
  const { common } = useAutoneTranslation();
  const { state, dispatch, config } = useAdvancedFiltersInternalContext();

  const { field, operator } = state.conditions[rowId];
  const { operatorOptions, fieldConfigs } = config;
  const isDisabled = !field;

  const currFieldConfig = useMemo(
    () => (field === undefined ? null : fieldConfigs[field]),
    [field, fieldConfigs],
  );

  const validFieldOperators = useMemo(() => {
    if (!currFieldConfig) {
      return [];
    } else {
      return currFieldConfig.operators.map((operator) => ({
        id: operator,
        translatedDisplayName: operatorOptions[operator],
      }));
    }
  }, [operatorOptions, currFieldConfig]);

  const handleUpdateColumnOperator = useCallback(
    (operator: string | undefined) => {
      dispatch({
        type: 'PATCH_CONDITION',
        payload: {
          rowId,
          condition: {
            operator,
          },
        },
      });
    },
    [dispatch, rowId],
  );

  /**
   * If the current operator is no longer valid for the selected field, clear it.
   */
  useLayoutEffect(() => {
    if (operator === undefined) return;
    else if (currFieldConfig?.operators.includes(operator)) return;

    dispatch({
      type: 'PATCH_CONDITION',
      payload: {
        rowId,
        condition: {
          operator: '',
        },
      },
    });
  }, [currFieldConfig, operator, dispatch, rowId]);

  /**
   * If there's only 1 option, auto-select it.
   */
  useLayoutEffect(() => {
    const numOptions = validFieldOperators.length;
    if (numOptions === 1) {
      handleUpdateColumnOperator(validFieldOperators[0].id);
    }
  }, [validFieldOperators, handleUpdateColumnOperator]);

  return (
    <Select
      disabled={isDisabled}
      value={operator}
      onValueChange={handleUpdateColumnOperator}
    >
      <div className="border-r border-border">
        <SelectTrigger
          id="select-advanced-filter-condition"
          data-testid="select-advanced-filter-condition"
          aria-label="Select advanced filter condition"
          className={cn(
            'focus-within:ring-0 focus-visible:outline-none focus-within:outline-none focus-visible:ring-0',
            'w-full h-full rounded-none border-none overflow-hidden',
          )}
        >
          <div className="w-full text-start overflow-hidden text-ellipsis text-sm">
            <SelectValue placeholder={common('advanced-filters.condition')} />
          </div>
        </SelectTrigger>
      </div>
      <SelectContent data-testid="advanced-filters-operator-select-content">
        {validFieldOperators.map(({ id, translatedDisplayName }) => (
          <SelectItem key={id} value={id} className="text-sm">
            {translatedDisplayName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
